import axios from "axios";

const API_URL = "https://srinivasafleet.com/api";

const authHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("access_token") ||
    "";

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
};

const normalizeFilePath = (url) => {
  if (!url) return "";

  let value = String(url).trim();

  value = value.replace(/^https?:\/\/[^/]+/i, "");
  value = value.replace(/^\/+/, "");

  if (value.startsWith("api/")) {
    value = value.substring(4);
  }

  if (value.startsWith("vehicle-documents/download/")) {
    value = value.substring(
      "vehicle-documents/download/".length
    );
  }

  const uploadsIndex = value.indexOf("uploads/");

  if (uploadsIndex >= 0) {
    value = value.substring(uploadsIndex);
  }

  return value;
};

const resolveFileUrl = (url) => {
  if (!url) return null;

  const path = normalizeFilePath(url);

  if (!path) return null;

  return `${API_URL}/vehicle-documents/download/${path}`;
};

const resolveDownloadUrl = (url, filename) => {
  if (!url) return null;

  const path = normalizeFilePath(url);

  if (!path) return null;

  let downloadUrl =
    `${API_URL}/vehicle-documents/download/${path}`;

  if (filename) {
    downloadUrl +=
      `?filename=${encodeURIComponent(filename)}`;
  }

  return downloadUrl;
};

const downloadFile = async (url, filename) => {
  const downloadUrl =
    resolveDownloadUrl(url, filename);

  if (!downloadUrl) {
    throw new Error("Invalid document URL");
  }

  const response = await axios.get(
    downloadUrl,
    {
      headers: authHeaders(),
      responseType: "blob",
    }
  );

  const responseType =
    response.headers?.["content-type"] || "";

  if (
    responseType.includes("application/json")
  ) {
    let message =
      "Unable to download document.";

    try {
      const text =
        await response.data.text();

      const data =
        JSON.parse(text);

      message =
        data?.detail ||
        message;
    } catch (_) {}

    throw new Error(message);
  }

  return {
    blob: response.data,
    filename:
      filename ||
      url?.split("/").pop() ||
      "document",
  };
};

const previewFile = async (url) => {
  const fileUrl = resolveFileUrl(url);

  if (!fileUrl) {
    throw new Error("Invalid document URL");
  }

  const response = await axios.get(
    fileUrl,
    {
      headers: authHeaders(),
      responseType: "blob",
    }
  );

  const responseType =
    response.headers?.["content-type"] || "";

  if (
    responseType.includes("application/json")
  ) {
    let message =
      "Unable to preview document.";

    try {
      const text =
        await response.data.text();

      const data =
        JSON.parse(text);

      message =
        data?.detail ||
        message;
    } catch (_) {}

    throw new Error(message);
  }

  const blob = response.data;

  const blobUrl =
    URL.createObjectURL(blob);

  return {
    blobUrl,
    contentType: responseType,
  };
};

const getServiceLocations = async () => {
  const response = await axios.get(
    `${API_URL}/master/service-locations`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const getBranches = async (locationId) => {
  const params =
    new URLSearchParams();

  if (locationId) {
    params.append(
      "location_id",
      locationId
    );
  }

  const query =
    params.toString();

  const response = await axios.get(
    `${API_URL}/master/branches${
      query ? `?${query}` : ""
    }`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const getCompanies = async () => {
  const response = await axios.get(
    `${API_URL}/master/companies`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const getVehicles = async (
  params = {}
) => {
  const query =
    new URLSearchParams();

  if (params.search) {
    query.append(
      "search",
      params.search
    );
  }

  if (params.service_location_id) {
    query.append(
      "service_location_id",
      params.service_location_id
    );
  }

  if (params.company_id) {
    query.append(
      "company_id",
      params.company_id
    );
  }

  if (params.limit) {
    query.append(
      "limit",
      params.limit
    );
  }

  const queryString =
    query.toString();

  const response = await axios.get(
    `${API_URL}/vehicle-documents/vehicles/search${
      queryString
        ? `?${queryString}`
        : ""
    }`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const getVehicleAutofill = async (
  vehicleId
) => {
  const response = await axios.get(
    `${API_URL}/vehicle-documents/autofill/${vehicleId}`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const getVehicleDocuments = async (
  vehicleId
) => {
  const response = await axios.get(
    `${API_URL}/vehicle-documents/${vehicleId}`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const getAllVehicleDocuments = async ({
  search,
  status = "active",
} = {}) => {
  const query =
    new URLSearchParams();

  if (search) {
    query.append(
      "search",
      search
    );
  }

  query.append(
    "status",
    status
  );

  const queryString =
    query.toString();

  const response = await axios.get(
    `${API_URL}/vehicle-documents${
      queryString
        ? `?${queryString}`
        : ""
    }`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const uploadDocumentFile = async (
  vehicleId,
  file
) => {
  const formData =
    new FormData();

  formData.append(
    "file",
    file
  );

  const response = await axios.post(
    `${API_URL}/vehicle-documents/upload/${vehicleId}`,
    formData,
    {
      headers: {
        ...authHeaders(),
        "Content-Type":
          "multipart/form-data",
      },
    }
  );

  return response.data;
};

const saveVehicleDocuments = async (
  payload
) => {
  const response = await axios.post(
    `${API_URL}/vehicle-documents`,
    payload,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const deleteVehicleDocuments = async (
  vehicleId
) => {
  const response = await axios.delete(
    `${API_URL}/vehicle-documents/${vehicleId}`,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const restoreVehicleDocuments = async (
  vehicleId
) => {
  const response = await axios.post(
    `${API_URL}/vehicle-documents/${vehicleId}/restore`,
    null,
    {
      headers: authHeaders(),
    }
  );

  return response.data;
};

const vehicleDocumentsService = {
  resolveFileUrl,
  resolveDownloadUrl,
  downloadFile,
  previewFile,
  getServiceLocations,
  getBranches,
  getCompanies,
  getVehicles,
  getVehicleAutofill,
  getVehicleDocuments,
  getAllVehicleDocuments,
  uploadDocumentFile,
  saveVehicleDocuments,
  deleteVehicleDocuments,
  restoreVehicleDocuments,
};

export default vehicleDocumentsService;